import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { ToolActivity } from '../../state/chatTypes';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import useStyles from './ThinkingIndicator.styles';

export type ThinkingIndicatorProps = {
  /** Optional className appended to the root element. */
  className?: string;
  /**
   * Current retry attempt (1, 2, or 3). When `attempt >= 2` the
   * indicator switches to `Trying again (N/max attempts)…` per
   * Figma node `15695:71925` / `15695:71973`. Attempt `1` (or
   * `undefined` / `0`) renders the plain `Thinking…` label per
   * Figma node `15554:18751`.
   */
  attempt?: 1 | 2 | 3;
  /**
   * Maximum number of attempts the indicator should display alongside
   * `attempt`. Ignored when `attempt < 2`. Defaults to `3`.
   */
  maxAttempts?: number;
  /**
   * In-flight tool activity from the slice. When `status === 'started'`
   * and `description` is non-empty after trim, the description is shown
   * verbatim (not through `t()`).
   */
  toolActivity?: ToolActivity | null;
  /**
   * True while a mid-stream SSE reconnect is in flight. Shows the
   * `chat.streaming.reconnecting` label unless a higher-precedence
   * state applies.
   */
  isReconnecting?: boolean;
};

/**
 * Resolves the visible status label using this precedence (highest first):
 *
 * 1. `attempt >= 2` → `chat.thinking.retrying`
 * 2. `isReconnecting` → `chat.streaming.reconnecting`
 * 3. `toolActivity.status === 'started'` with non-empty trimmed
 *    `description` → `description` verbatim (not translated)
 * 4. Default → `chat.thinking.label`
 */
const resolveThinkingLabel = (params: {
  attempt?: 1 | 2 | 3;
  maxAttempts: number;
  toolActivity?: ToolActivity | null;
  isReconnecting?: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}): string => {
  const { attempt, maxAttempts, toolActivity, isReconnecting, t } = params;
  const isRetrying =
    typeof attempt === 'number' && attempt >= 2 && maxAttempts > 0;

  if (isRetrying) {
    return t('chat.thinking.retrying', { current: attempt, max: maxAttempts });
  }
  if (isReconnecting === true) {
    return t('chat.streaming.reconnecting');
  }
  if (
    toolActivity?.status === 'started' &&
    toolActivity.description.trim().length > 0
  ) {
    return toolActivity.description;
  }
  return t('chat.thinking.label');
};

/**
 * "Thinking…" status indicator rendered between the latest user
 * message and the not-yet-streamed assistant reply while
 * `useConversation().isStreaming === true`.
 *
 * The label is the localized "Thinking" word with an animated
 * ellipsis appended via a CSS pseudo-element. The pseudo-content is
 * outside the accessibility tree, so the polite live region on the
 * root `<output>` announces the static label (no dot churn).
 *
 * When `attempt >= 2`, the indicator switches to the retry-attempts
 * variant and renders `Trying again (N/max attempts)…` for the
 * simulated 3-attempt failure flow. Attempt `1` is the very first
 * try and renders the plain `Thinking…` label.
 */
function ThinkingIndicator({
  className,
  attempt,
  maxAttempts = 3,
  toolActivity = null,
  isReconnecting = false,
}: ThinkingIndicatorProps) {
  const { classes, cx } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);

  const label = resolveThinkingLabel({
    attempt,
    maxAttempts,
    toolActivity,
    isReconnecting,
    t,
  });

  return (
    <Box
      component="output"
      className={cx(classes.container, className)}
      data-testid="ai-sidekick-thinking-indicator"
      aria-live="polite"
      aria-atomic="true"
    >
      <Typography className={classes.text} component="span">
        {label}
      </Typography>
    </Box>
  );
}

export default ThinkingIndicator;
